/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type {
  ActiveCell,
  ActiveCellDetail,
  ScrollDetail,
  SizingInfo,
  TableColumn,
  ViewportInfo
} from '../Table';

import { findElementByKey } from '#utils/PRIVATE_collectionUtils';
import { getRowKey } from '#PRIVATE_TableCommon/utils/DomUtils';
import {
  getBoundaryColumnKey,
  getElementFromCell,
  getFirstRenderedRowKey,
  getLastRenderedRowKey,
  getRowIndexForRowKey,
  getRowKeyForRowIndex,
  hasFooterCells,
  hasLoadMoreCell,
  hasNoDataCell,
  isAutoFocusCell,
  TABLE_ROW_SELECTOR
} from './TableDomUtils';
import { cellComparator } from './TableFocusUtils';
import { getScrollerBottom, getVerticalScrollPosition } from './TableScrollUtils';

/**
 * Helper function to handle an 'Up' or 'Down' arrow key press.
 * Up - Moves to the same cell in the previous row (including headers and footers). No op if already on the first row.
 * Down - Moves to the same cell in the next row (including headers and footers). No op if already on the last row.
 */
export function handleAdjacentRowGesture<K extends string | number, D, C extends string>(
  rootElement: HTMLDivElement,
  tableId: string,
  columnsArray: TableColumn<K, D, C>[],
  initialCell: ActiveCell<K, C>,
  onActiveCellChanged: (
    detail: ActiveCellDetail<K, C>,
    isAutoFocus?: boolean,
    isSelectionExtension?: boolean,
    skipScrollCellIntoView?: boolean
  ) => void,
  isPrevious: boolean,
  isExtendSelectionGesture: boolean,
  hideTooltip: (isImmediate?: boolean) => void,
  updateDeferredScrollDetail: (scrollDetail: ScrollDetail<K, C>) => void
) {
  const firstColumnKey = getBoundaryColumnKey<K, D, C>(columnsArray, true);
  if (firstColumnKey == null) {
    return;
  }
  let newCell = initialCell;
  const cellType = initialCell.type;
  if (cellType === 'noData') {
    const newColumnKey = firstColumnKey;
    if (isPrevious) {
      newCell = { columnKey: newColumnKey, type: 'header' };
      onActiveCellChanged({ value: newCell }, isAutoFocusCell(rootElement, tableId, newCell));
    } else if (hasFooterCells(rootElement, tableId)) {
      newCell = { columnKey: newColumnKey, type: 'footer' };
      onActiveCellChanged({ value: newCell }, isAutoFocusCell(rootElement, tableId, newCell));
    }
  } else if (cellType === 'loading') {
    if (isPrevious) {
      newCell = { columnKey: initialCell.columnKey, type: 'header' };
      onActiveCellChanged({ value: newCell }, isAutoFocusCell(rootElement, tableId, newCell));
    } else if (hasFooterCells(rootElement, tableId)) {
      newCell = { columnKey: initialCell.columnKey, type: 'footer' };
      onActiveCellChanged({ value: newCell }, isAutoFocusCell(rootElement, tableId, newCell));
    }
  } else if (cellType === 'loadMore') {
    if (isPrevious) {
      const rowKey = getLastRenderedRowKey<K>(rootElement, tableId);
      if (rowKey != null) {
        newCell = { rowKey: rowKey, columnKey: initialCell.columnKey, type: 'data' };
        onActiveCellChanged({ value: newCell }, isAutoFocusCell(rootElement, tableId, newCell));
      }
    } else if (hasFooterCells(rootElement, tableId)) {
      newCell = { columnKey: initialCell.columnKey, type: 'footer' };
      onActiveCellChanged({ value: newCell }, isAutoFocusCell(rootElement, tableId, newCell));
    }
  } else if (cellType !== 'pending') {
    if (cellType === 'data') {
      const currentRowKey = initialCell.rowKey;
      const currentRowElement = findElementByKey(
        rootElement,
        currentRowKey,
        `[${TABLE_ROW_SELECTOR}='${tableId}']`
      );
      if (currentRowElement != null) {
        const rowElements = [
          ...rootElement.querySelectorAll(`[${TABLE_ROW_SELECTOR}='${tableId}']`)
        ];
        const currentRowIndex = rowElements.indexOf(currentRowElement);
        let adjacentRowElement;
        if (isPrevious && currentRowIndex > 0) {
          adjacentRowElement = rowElements[currentRowIndex - 1] as HTMLElement;
        } else if (!isPrevious && currentRowIndex < rowElements.length - 1) {
          adjacentRowElement = rowElements[currentRowIndex + 1] as HTMLElement;
        }
        if (adjacentRowElement != null) {
          const rowKey = getRowKey<K>(adjacentRowElement);
          if (rowKey != null) {
            newCell = { rowKey: rowKey, columnKey: initialCell.columnKey, type: 'data' };
            onActiveCellChanged(
              { value: newCell },
              isAutoFocusCell(rootElement, tableId, newCell),
              isExtendSelectionGesture
            );
          }
        } else if (!isExtendSelectionGesture) {
          if (isPrevious) {
            newCell = { columnKey: initialCell.columnKey, type: 'header' };
            onActiveCellChanged({ value: newCell }, isAutoFocusCell(rootElement, tableId, newCell));
          } else if (hasLoadMoreCell(rootElement, tableId)) {
            newCell = { columnKey: initialCell.columnKey, type: 'loadMore' };
            if (getElementFromCell(rootElement, tableId, newCell) != null) {
              onActiveCellChanged(
                { value: newCell },
                isAutoFocusCell(rootElement, tableId, newCell)
              );
            }
          } else if (hasFooterCells(rootElement, tableId)) {
            newCell = { columnKey: initialCell.columnKey, type: 'footer' };
            onActiveCellChanged({ value: newCell }, isAutoFocusCell(rootElement, tableId, newCell));
          }
        }
      }
    } else if (cellType === 'header' && !isPrevious) {
      const rowKey = getFirstRenderedRowKey<K>(rootElement, tableId);
      if (rowKey != null) {
        newCell = { rowKey: rowKey, columnKey: initialCell.columnKey, type: 'data' };
        onActiveCellChanged({ value: newCell }, isAutoFocusCell(rootElement, tableId, newCell));
      } else if (hasNoDataCell(rootElement, tableId)) {
        newCell = { type: 'noData' };
        onActiveCellChanged({ value: newCell }, isAutoFocusCell(rootElement, tableId, newCell));
      } else {
        newCell = { columnKey: initialCell.columnKey, type: 'loading' };
        if (getElementFromCell(rootElement, tableId, newCell) != null) {
          onActiveCellChanged({ value: newCell }, isAutoFocusCell(rootElement, tableId, newCell));
        } else if (hasFooterCells(rootElement, tableId)) {
          newCell = { columnKey: initialCell.columnKey, type: 'footer' };
          onActiveCellChanged({ value: newCell }, isAutoFocusCell(rootElement, tableId, newCell));
        }
      }
    } else if (cellType === 'footer' && isPrevious) {
      if (hasNoDataCell(rootElement, tableId)) {
        newCell = { type: 'noData' };
        onActiveCellChanged({ value: newCell }, isAutoFocusCell(rootElement, tableId, newCell));
      } else {
        newCell = { columnKey: initialCell.columnKey, type: 'loading' };
        if (getElementFromCell(rootElement, tableId, newCell) != null) {
          onActiveCellChanged({ value: newCell }, isAutoFocusCell(rootElement, tableId, newCell));
        } else {
          newCell = { columnKey: initialCell.columnKey, type: 'loadMore' };
          if (getElementFromCell(rootElement, tableId, newCell) != null) {
            onActiveCellChanged({ value: newCell }, isAutoFocusCell(rootElement, tableId, newCell));
          } else {
            const rowKey = getLastRenderedRowKey<K>(rootElement, tableId);
            if (rowKey != null) {
              newCell = { rowKey: rowKey, columnKey: initialCell.columnKey, type: 'data' };
              onActiveCellChanged(
                { value: newCell },
                isAutoFocusCell(rootElement, tableId, newCell)
              );
            } else {
              newCell = { columnKey: initialCell.columnKey, type: 'header' };
              onActiveCellChanged(
                { value: newCell },
                isAutoFocusCell(rootElement, tableId, newCell)
              );
            }
          }
        }
      }
    }
  }
  if (!cellComparator(initialCell, newCell)) {
    hideTooltip(true);
  } else {
    updateDeferredScrollDetail({ cell: newCell, locationX: 'inView', locationY: 'inView' });
  }
}

/**
 * Helper function to handle a 'PageUp' or 'PageDown' arrow key press.
 * PageUp - Moves to the same cell in the first visible row in the current viewport,
 * and scrolls the Table so it becomes the last visible row in the viewport when able.
 * PageDown - Moves to the same cell in the last visible row in the current viewport,
 * and scrolls the Table so it becomes the first visible row in the viewport when able.
 */
export function handlePageRowGesture<K extends string | number, C extends string>(
  rootElement: HTMLDivElement,
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  tableId: string,
  sizingInfo: SizingInfo,
  initialCell: ActiveCell<K, C>,
  onActiveCellChanged: (
    detail: ActiveCellDetail<K, C>,
    isAutoFocus?: boolean,
    isSelectionExtension?: boolean,
    skipScrollCellIntoView?: boolean
  ) => void,
  isPrevious: boolean,
  hideTooltip: (isImmediate?: boolean) => void,
  updateDeferredScrollDetail: (scrollDetail: ScrollDetail<K, C>) => void
) {
  if (initialCell.type !== 'noData' && initialCell.type !== 'pending') {
    const rowElements = rootElement.querySelectorAll(`[${TABLE_ROW_SELECTOR}='${tableId}']`);
    if (rowElements.length > 0) {
      const verticalScrollPosition = getVerticalScrollPosition(
        rootElement,
        scrollerElement,
        viewportInfo,
        tableId
      );
      const verticalRowIndex = getRowIndexForRowKey(
        rootElement,
        tableId,
        verticalScrollPosition.rowKey!
      );
      let targetRowIndex;
      if (isPrevious) {
        if (verticalScrollPosition.offsetY === 0) {
          targetRowIndex = Math.max(verticalRowIndex! - 1, 0);
        } else {
          targetRowIndex = verticalRowIndex!;
        }
      } else {
        const scrollerBottom = getScrollerBottom(
          rootElement,
          scrollerElement,
          viewportInfo.viewportConfig,
          sizingInfo,
          tableId
        );
        targetRowIndex = verticalRowIndex!;
        while (rowElements.length > targetRowIndex + 1) {
          targetRowIndex += 1;
          const elementBottom = rowElements[targetRowIndex].getBoundingClientRect().bottom;
          if (elementBottom > scrollerBottom) {
            break;
          }
        }
      }
      const newRowKey = getRowKeyForRowIndex<K>(rootElement, tableId, targetRowIndex);
      if (newRowKey != null) {
        const newCell: ActiveCell<K, C> = {
          rowKey: newRowKey,
          columnKey: initialCell.columnKey,
          type: 'data'
        };
        if (newRowKey !== initialCell.rowKey) {
          onActiveCellChanged(
            { value: newCell },
            isAutoFocusCell(rootElement, tableId, newCell),
            false,
            true
          );
        }
        if (!cellComparator(initialCell, newCell)) {
          hideTooltip(true);
        }
        updateDeferredScrollDetail({
          cell: newCell,
          locationX: 'inView',
          locationY: isPrevious ? 'bottom' : 'top'
        });
      }
    }
  }
}

/**
 * Helper function to get the column key next to the given column key in the specified direction. Returns
 * null if there is no adjacent column in the direction specified.
 */
function _getAdjacentColumnKey<K, D, C>(
  initialKey: C,
  columnsArray: TableColumn<K, D, C>[],
  isPrevious: boolean
) {
  let currentIndex = -1;
  const columnsCount = columnsArray.length;
  for (let i = 0; i < columnsCount; i++) {
    if (columnsArray[i].key === initialKey) {
      currentIndex = i;
      break;
    }
  }
  if (isPrevious && currentIndex !== 0) {
    return columnsArray[currentIndex - 1].key;
  } else if (!isPrevious && currentIndex !== columnsCount - 1) {
    return columnsArray[currentIndex + 1].key;
  }
  return undefined;
}

/**
 * Helper function to handle a 'Previous' or 'Next' arrow key press.
 * Previous - Moves to the previous cell in the current row. No op if already on the first cell in that row.
 * Next - Moves to the next cell in the current row. No op if already on the last cell in that row.
 */
export function handleAdjacentColumnGesture<K extends string | number, D, C extends string>(
  rootElement: HTMLDivElement,
  tableId: string,
  columnsArray: TableColumn<K, D, C>[],
  initialCell: ActiveCell<K, C>,
  onActiveCellChanged: (
    detail: ActiveCellDetail<K, C>,
    isAutoFocus?: boolean,
    isSelectionExtension?: boolean,
    skipScrollCellIntoView?: boolean
  ) => void,
  isPrevious: boolean,
  hideTooltip: (isImmediate?: boolean) => void,
  updateDeferredScrollDetail: (scrollDetail: ScrollDetail<K, C>) => void
) {
  let newCell = initialCell;
  const cellType = initialCell.type;
  if (cellType === 'data' || cellType === 'header' || cellType === 'footer') {
    const initialKey = initialCell.columnKey;
    const newKey = _getAdjacentColumnKey(initialKey, columnsArray, isPrevious);
    if (newKey != null) {
      if (cellType === 'data') {
        newCell = { rowKey: initialCell.rowKey, columnKey: newKey, type: cellType };
        onActiveCellChanged({ value: newCell }, isAutoFocusCell(rootElement, tableId, newCell));
      } else {
        newCell = { columnKey: newKey, type: cellType };
        onActiveCellChanged({ value: newCell }, isAutoFocusCell(rootElement, tableId, newCell));
      }
    }
  }
  if (!cellComparator(initialCell, newCell)) {
    hideTooltip(true);
  } else {
    updateDeferredScrollDetail({ cell: newCell, locationX: 'inView', locationY: 'inView' });
  }
}

/**
 * Helper function to handle a 'Home' or 'End' key press.
 * Home - Jumps to the first cell in the current row. No op if already on the first cell in that row.
 * Ctrl/Cmd + Home - Jumps to the first cell in the first data row. If no data rows are present,
 * jumps to the first cell in the current region (header or footer). No op if already on that cell.
 * End - Jumps to the last cell in the current row. No op if already on the last cell in that row.
 * Ctrl/Cmd + End - Jumps to the last cell in the last data row. If no data rows are present,
 * jumps to the last cell in the current region (header or footer). No op if already on that cell.
 */
export function handleJumpColumnGesture<K extends string | number, D, C extends string>(
  rootElement: HTMLDivElement,
  tableId: string,
  columnsArray: TableColumn<K, D, C>[],
  initialCell: ActiveCell<K, C>,
  onActiveCellChanged: (
    detail: ActiveCellDetail<K, C>,
    isAutoFocus?: boolean,
    isSelectionExtension?: boolean,
    skipScrollCellIntoView?: boolean
  ) => void,
  isPrevious: boolean,
  includeRows: boolean,
  hideTooltip: (isImmediate?: boolean) => void,
  updateDeferredScrollDetail: (scrollDetail: ScrollDetail<K, C>) => void
) {
  const newKey = getBoundaryColumnKey<K, D, C>(columnsArray, isPrevious);
  if (newKey == null) {
    return;
  }
  let newCell = initialCell;
  const cellType = initialCell.type;
  if (cellType === 'data' || cellType === 'header' || cellType === 'footer') {
    const initialKey = initialCell.columnKey;
    if (includeRows) {
      if (hasNoDataCell(rootElement, tableId)) {
        newCell = { type: 'noData' };
        onActiveCellChanged({ value: newCell }, isAutoFocusCell(rootElement, tableId, newCell));
      } else {
        const newRowKey = isPrevious
          ? getFirstRenderedRowKey<K>(rootElement, tableId)
          : getLastRenderedRowKey<K>(rootElement, tableId);
        if (newRowKey != null) {
          newCell = { rowKey: newRowKey, columnKey: newKey, type: 'data' };
          onActiveCellChanged({ value: newCell }, isAutoFocusCell(rootElement, tableId, newCell));
        }
      }
    } else if (newKey !== initialKey) {
      if (cellType === 'data') {
        newCell = { rowKey: initialCell.rowKey, columnKey: newKey, type: cellType };
        onActiveCellChanged({ value: newCell }, isAutoFocusCell(rootElement, tableId, newCell));
      } else {
        newCell = { columnKey: newKey, type: cellType };
        onActiveCellChanged({ value: newCell }, isAutoFocusCell(rootElement, tableId, newCell));
      }
    }
  }
  if (!cellComparator(initialCell, newCell)) {
    hideTooltip(true);
  } else {
    updateDeferredScrollDetail({ cell: newCell, locationX: 'inView', locationY: 'inView' });
  }
}
